/**
 * useGlossary Hook
 *
 * Hook for managing glossary modal state and fetching glossary terms
 */

import { useState, useEffect, useCallback } from 'react';
import { helpAPI } from '../../../lib/api/help';
import { useHelp } from '../HelpContext';
import type { GlossaryTerm } from '../types';

export function useGlossary() {
  const {
    isGlossaryOpen,
    glossarySearchTerm,
    openGlossary,
    closeGlossary,
  } = useHelp();

  const [terms, setTerms] = useState<GlossaryTerm[]>([]);
  const [termsByCategory, setTermsByCategory] = useState<Record<string, GlossaryTerm[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<GlossaryTerm[]>([]);

  // Fetch all terms when glossary opens
  useEffect(() => {
    if (isGlossaryOpen && terms.length === 0) {
      setIsLoading(true);
      helpAPI.getAllGlossaryTerms()
        .then(response => {
          // Handle paginated response - extract results array
          const responseData = response.data as GlossaryTerm[] | { results: GlossaryTerm[] };
          const termsData: GlossaryTerm[] = Array.isArray(responseData)
            ? responseData
            : (responseData.results || []);
          setTerms(termsData);
          // Group by category
          const grouped = termsData.reduce((acc: Record<string, GlossaryTerm[]>, term: GlossaryTerm) => {
            if (!acc[term.category]) {
              acc[term.category] = [];
            }
            acc[term.category].push(term);
            return acc;
          }, {} as Record<string, GlossaryTerm[]>);
          setTermsByCategory(grouped);
        })
        .catch(() => {
          // Silently fail
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isGlossaryOpen, terms.length]);

  // Filter terms when search changes
  useEffect(() => {
    if (!glossarySearchTerm) {
      setSearchResults(terms);
      return;
    }

    const query = glossarySearchTerm.toLowerCase();
    const filtered = terms.filter(term =>
      term.term.toLowerCase().includes(query) ||
      term.abbreviation?.toLowerCase().includes(query) ||
      term.definition.toLowerCase().includes(query)
    );
    setSearchResults(filtered);
  }, [glossarySearchTerm, terms]);

  const lookupTerm = useCallback(async (termOrAbbreviation: string): Promise<GlossaryTerm | null> => {
    try {
      const response = await helpAPI.lookupGlossaryTerm(termOrAbbreviation);
      return response.data;
    } catch {
      return null;
    }
  }, []);

  return {
    isOpen: isGlossaryOpen,
    open: openGlossary,
    close: closeGlossary,
    terms: searchResults,
    termsByCategory,
    isLoading,
    searchTerm: glossarySearchTerm,
    lookupTerm,
  };
}
